// Situação do veículo (at-37, item 39) — fonte única no frontend.
//
// Até a at-37 a substituição veicular gravava `deletedAt` no carro antigo, então
// "carro vivo" era só `!deletedAt`. Agora são DOIS estados distintos:
//
//   situacao = 'SUBSTITUIDO' -> saiu de circulação por substituição; o registro
//                               continua existindo e é EXIBIDO nas telas de
//                               consulta (adesão) e na carteira, sem ações.
//   deletedAt preenchido     -> exclusão de verdade.
//
// ⚠️ Toda tela que precisa de "veículo em circulação" deve usar `isCarroVivo`.
// Continuar filtrando só por `deletedAt` volta a deixar o veículo substituído
// entrar nos fluxos operacionais (produtos, edição, adesão) — foi exatamente o
// que o item 39 removeu.

import type { VarianteChip } from '../ui/primitivos/Chip';

export const SITUACAO_CARRO_SUBSTITUIDO = 'SUBSTITUIDO';

export type CarroComSituacao = {
  deletedAt?: string | null;
  situacao?: string | null;
};

/** Veículo substituído por outro (fora de circulação, mas não excluído). */
export const isCarroSubstituido = (carro?: CarroComSituacao | null): boolean =>
  carro?.situacao === SITUACAO_CARRO_SUBSTITUIDO;

/** Veículo em circulação: nem excluído, nem substituído. */
export const isCarroVivo = (carro?: CarroComSituacao | null): boolean =>
  !!carro && !carro.deletedAt && !isCarroSubstituido(carro);

export type RotuloSituacaoCarro = {
  texto: string;
  cor: string;
  fundo: string;
  /**
   * Variante do `Chip` do design system (at-56).
   *
   * ⚠️ Campo ADITIVO: `cor`/`fundo` continuam porque a `/carteira` e o painel de
   * vistoria ainda pintam à mão com CSS legado. As telas migradas usam a
   * variante; quando a frente D migrar as outras, `cor`/`fundo` saem.
   */
  variante: VarianteChip;
};

/**
 * Rótulo visual do estado do veículo. Fica aqui (e não em cada tela) para os
 * textos e cores não divergirem entre a consulta de adesão e a carteira.
 */
export const rotuloSituacaoCarro = (
  carro?: CarroComSituacao | null,
): RotuloSituacaoCarro => {
  if (carro?.deletedAt) {
    return { texto: 'Excluído', cor: '#c0392b', fundo: '#f5f5f5', variante: 'perigo' };
  }

  if (isCarroSubstituido(carro)) {
    return { texto: 'Substituído', cor: '#7f6000', fundo: '#fdf3d4', variante: 'pendente' };
  }

  return { texto: 'Ativo', cor: '#1e7a46', fundo: '#e9f7ef', variante: 'ok' };
};
